import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'

import { AutomationOption } from '../constants/data/common'

import { Container } from '../styles/PageLayout'
import { TitleM, BodyM, BodyS } from '../styles/Typography'
import { ButtonOutline } from '../styles/components/ButtonStyled'
import { AutomationOptionsStyled } from '../styles/components/AutomationOptionsStyled'

import AutomationOptionsAlarm from './icons/automation-options-alarm'
import AutomationOptionsLocation from './icons/automation-options-location'
import AutomationOptionsDeviceAction from './icons/automation-options-device-action'
import AutomationOptionsVoiceCommand from './icons/automation-options-voice-command'
import AutomationOptionsVoiceMusic from './icons/automation-options-music'
import AutomationOptionsTime from './icons/automation-options-time'
import AutomationOptionsPlayAndControlMedia from './icons/automation-options-play-and-control-media'
import AutomationOptionsAdjustAssistantVolume from './icons/automation-options-adjust-assistant-volume'
import AutomationOptionsAdjustPhoneSettings from './icons/automation-options-adjust-phone-settings'
import AutomationOptionsInfoAndReminders from './icons/automation-options-info-and-reminders'
import AutomationOptionsCommunicateAnnounce from './icons/automation-options-communicate-announce'
import AutomationOptionsPushButton from './icons/automation-options-push-button'
import AutomationOptionsKnxOrIotSensors from './icons/automation-options-knx-or-iot-sensors'
import AutomationOptionsHomePod from './icons/automation-options-home-pod'
import AutomationOptionsAirplay from './icons/automation-options-airplay'
import AutomationOptionsAppleTv from './icons/automation-options-apple-tv'
import AutomationOptionsScene from './icons/automation-options-scene'
import AutomationOptionsIfttt from './icons/automation-options-ifttt'
import AutomationOptionsNotification from './icons/automation-options-notification'
import AutomationOptionsFireTv from './icons/automation-options-fire-tv'
import AutomationOptionsCalendar from './icons/automation-options-calendar'
import AutomationOptionsSunsetSunrise from './icons/automation-options-sunset-sunrise'

/**
 * The automation options block.
 * @param {ComponentProps} props
 * @param {string} props.title
 * @param {string[]} props.options
 * @param {number} props.activeOption
 * @param {string} props.type
 * @returns {FunctionComponent}
 */
const AutomationOptions = ({ title, options, activeOption, type }) => {
  const intl = useIntl()

  const [optionsGroup, setOptionsGroup] = useState(activeOption)

  return (
    <AutomationOptionsStyled $alignCenter>
      <Container>
        <div className="content-box">
          <Container $column $mobileColumn $alignItems="center">
            <TitleM className="title">{title}</TitleM>

            {options.length > 1 && (
              <div className="btn-group">
                {options.includes(AutomationOption.GoogleHome) && (
                  <ButtonOutline
                    className={optionsGroup === AutomationOption.GoogleHome && 'active'}
                    onClick={() => {
                      setOptionsGroup(AutomationOption.GoogleHome)
                    }}
                  >
                    {intl.formatMessage({ id: 'google_home' })}
                  </ButtonOutline>
                )}
                {options.includes(AutomationOption.AppleHomeKit) && (
                  <ButtonOutline
                    className={optionsGroup === AutomationOption.AppleHomeKit && 'active'}
                    onClick={() => {
                      setOptionsGroup(AutomationOption.AppleHomeKit)
                    }}
                  >
                    {intl.formatMessage({ id: 'apple_home_kit' })}
                  </ButtonOutline>
                )}
                {options.includes(AutomationOption.AmazonAlexa) && (
                  <ButtonOutline
                    className={optionsGroup === AutomationOption.AmazonAlexa && 'active'}
                    onClick={() => {
                      setOptionsGroup(AutomationOption.AmazonAlexa)
                    }}
                  >
                    {intl.formatMessage({ id: 'amazon_alexa' })}
                  </ButtonOutline>
                )}
              </div>
            )}

            <div className="options-group">
              <div className="options">
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automation_options_group_1' })}</BodyM>
                {options.includes(AutomationOption.GoogleHome) && optionsGroup === AutomationOption.GoogleHome && (
                  <div className="items">
                    <div className="item">
                      <AutomationOptionsVoiceCommand className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_voice_command' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsTime className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_time' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsSunsetSunrise className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_sunset_sunrise' })}</BodyS>
                    </div>
                  </div>
                )}
                {options.includes(AutomationOption.AppleHomeKit) && optionsGroup === AutomationOption.AppleHomeKit && (
                  <div className="items">
                    <div className="item">
                      <AutomationOptionsVoiceCommand className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_voice_command' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsTime className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_time' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsAlarm className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_alarm' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsPushButton className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_knx_push_button' }, { type })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsKnxOrIotSensors className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_knx_or_iot_sensors' }, { type })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsLocation className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_location' })}</BodyS>
                    </div>
                  </div>
                )}
                {options.includes(AutomationOption.AmazonAlexa) && optionsGroup === AutomationOption.AmazonAlexa && (
                  <div className="items">
                    <div className="item">
                      <AutomationOptionsVoiceCommand className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_voice_command' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsLocation className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_location' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsAlarm className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_alarm' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsTime className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_time' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsDeviceAction className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_device_action' })}</BodyS>
                    </div>
                  </div>
                )}
              </div>
              <div className="options">
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automation_options_group_2' })}</BodyM>
                {options.includes(AutomationOption.GoogleHome) && optionsGroup === AutomationOption.GoogleHome && (
                  <div className="items">
                    <div className="item">
                      <AutomationOptionsDeviceAction className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_device_action' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsPlayAndControlMedia className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_play_and_control_media' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsAdjustAssistantVolume className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_adjust_assistant_volume' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsAdjustPhoneSettings className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_adjust_phone_settings' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsCommunicateAnnounce className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_communicate_announce' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsInfoAndReminders className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_info_and_reminders' })}</BodyS>
                    </div>
                  </div>
                )}
                {options.includes(AutomationOption.AppleHomeKit) && optionsGroup === AutomationOption.AppleHomeKit && (
                  <div className="items">
                    <div className="item">
                      <AutomationOptionsDeviceAction className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_device_action' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsHomePod className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_home_pod' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsAirplay className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_airplay' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsAppleTv className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_apple_tv' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsScene className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_scene' })}</BodyS>
                    </div>
                  </div>
                )}
                {options.includes(AutomationOption.AmazonAlexa) && optionsGroup === AutomationOption.AmazonAlexa && (
                  <div className="items">
                    <div className="item">
                      <AutomationOptionsDeviceAction className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_device_action' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsVoiceMusic className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_music' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsIfttt className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_ifttt' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsNotification className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_notification' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsFireTv className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_fire_tv' })}</BodyS>
                    </div>
                    <div className="item">
                      <AutomationOptionsCalendar className="item-icon" />
                      <BodyS>{intl.formatMessage({ id: 'block_automation_options_calendar' })}</BodyS>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Container>
        </div>
      </Container>
    </AutomationOptionsStyled>
  )
}

AutomationOptions.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  type: PropTypes.string,
}

export default AutomationOptions
